import React from 'react';
import { MoreInfoBlob } from '@insurancemenu/imenu-components-lib';
import { capitalize } from '@insurancemenu/imenu-components-lib/dist/helpers';
import {
  BlobContactFieldsStyled,
  BlobLabelStyled,
  BlobPlaceholderStyled,
  BlobPopoverStyled,
  BlobRowStyled,
  BlobSectionTitleStyled,
  BlobValueStyled,
} from './styles';

interface InfoFieldsProps {
  infoMap: Record<string, string> | null;
}

export interface GroupContextMoreInfoBlobInput {
  /** Shown as the blob title (e.g. group name). */
  blobTitle: string;
  /** Optional key/value rows under “General information” (e.g. group name, effective date). */
  generalInfoMap: Record<string, string>;
  /** Optional key/value rows under “Contact information” (e.g. from `buildGroupContactInfoMap`). */
  contactInfoMap?: Record<string, string> | null;
}

const InfoFields: React.FC<InfoFieldsProps> = ({ infoMap }) => {
  const rows = Object.entries(infoMap ?? {}).filter(([, value]) => value);

  return (
    <BlobContactFieldsStyled>
      {rows.length > 0 ? (
        rows.map(([key, value]) => (
          <BlobRowStyled key={key}>
            <BlobLabelStyled>{capitalize(key)}:</BlobLabelStyled>
            <BlobValueStyled>{value}</BlobValueStyled>
          </BlobRowStyled>
        ))
      ) : (
        <BlobPlaceholderStyled>No details available</BlobPlaceholderStyled>
      )}
    </BlobContactFieldsStyled>
  );
};

/**
 * Builds `moreInfoBlob` props for `NoSidebarPageLayout` / `SidebarPageLayout`.
 * Pages supply arbitrary maps so employee flows, initiate proposal, etc. can show different rows.
 */
export function buildGroupContextMoreInfoBlobProps({
  blobTitle,
  generalInfoMap,
  contactInfoMap = null,
}: GroupContextMoreInfoBlobInput): React.ComponentProps<typeof MoreInfoBlob> {
  return {
    title: blobTitle,
    infoTextTitle: 'More Info',
    popOver: {
      content: (
        <BlobPopoverStyled>
          <div>
            <BlobSectionTitleStyled>General Information:</BlobSectionTitleStyled>
            <InfoFields infoMap={generalInfoMap} />
          </div>

          <div>
            <BlobSectionTitleStyled>Contact Information:</BlobSectionTitleStyled>
            <InfoFields infoMap={contactInfoMap} />
          </div>
        </BlobPopoverStyled>
      ),
    },
  };
}
